import React from 'react';
import {
  Paper,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Tooltip,
  InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Prevent mouse wheel scrolling on number inputs
const preventWheelChange = (e) => {
  e.target.blur();
};

const FuelSourceItem = ({ 
  source, 
  index, 
  isAdditional, 
  onFuelSourceChange,
  onRemoveFuelSource, 
  gelFrequency, 
  gelFrequencyUnit 
}) => {
  // Handle change to a field in the fuel source
  const handleChange = (field, value) => {
    onFuelSourceChange(index, field, value, isAdditional);
  };

  // Get unit label based on source type
  const getUnitLabel = () => {
    switch(source.type) {
      case 'gel': return 'gel';
      case 'drinkMix': return 'scoop';
      default: return 'serving';
    }
  };

  // Get consumption unit label based on source type
  const getConsumptionLabel = () => {
    switch(source.type) {
      case 'gel': return 'Gels';
      case 'drinkMix': return 'Scoops';
      default: return 'Servings';
    }
  };

  // Get frequency label
  const getFrequencyLabel = () => {
    const unit = gelFrequencyUnit === 'miles' ? 'mi' : 
                 gelFrequencyUnit === 'kilometers' ? 'km' : 'hr';
    return `/${gelFrequency}${unit}`;
  };

  // Render a more compact layout for carbs per hour mode
  if (!isAdditional) {
    return (
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 2, 
          borderRadius: 1,
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1, pr: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={source.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="gel">Gel</MenuItem>
                    <MenuItem value="drinkMix">Drink Mix</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name"
                  value={source.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder={`e.g., ${source.type === 'gel' ? 'GU Energy Gel' : source.type === 'drinkMix' ? 'Tailwind' : 'Trail Mix'}`}
                />
              </Grid>
              
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Carbs per Unit"
                  value={source.carbsPerUnit}
                  onChange={(e) => handleChange('carbsPerUnit', e.target.value)}
                  type="text"
                  onWheel={preventWheelChange}
                  InputProps={{
                    inputProps: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          g/{getUnitLabel()}
                        </Typography>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={`${getConsumptionLabel()}/hour`}
                  value={source.unitsPerHour}
                  onChange={(e) => handleChange('unitsPerHour', e.target.value)}
                  type="text"
                  onWheel={preventWheelChange}
                  InputProps={{
                    inputProps: {
                      inputMode: "numeric",
                      pattern: "[0-9]*[.][0-9]*",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          /hr
                        </Typography>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          
          <IconButton
            size="small"
            onClick={() => onRemoveFuelSource(index, isAdditional)}
            sx={{ mt: 1 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    );
  }

  // Layout for gel frequency mode (additional fuel sources)
  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        borderRadius: 1,
        position: 'relative'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, pr: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={source.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  label="Type"
                >
                  <MenuItem value="gel">Gel</MenuItem>
                  <MenuItem value="drinkMix">Drink Mix</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={source.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={`e.g., ${source.type === 'gel' ? 'GU Energy Gel' : source.type === 'drinkMix' ? 'Tailwind' : 'Trail Mix'}`}
              />
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Carbs per Unit"
                value={source.carbsPerUnit}
                onChange={(e) => handleChange('carbsPerUnit', e.target.value)}
                type="text"
                onWheel={preventWheelChange}
                InputProps={{
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        g/{getUnitLabel()}
                      </Typography>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label={`${getConsumptionLabel()}/frequency`}
                value={source.unitsPerFrequency}
                onChange={(e) => handleChange('unitsPerFrequency', e.target.value)}
                type="text"
                onWheel={preventWheelChange}
                InputProps={{
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {getFrequencyLabel()}
                      </Typography>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </Box>
        
        <IconButton
          size="small"
          onClick={() => onRemoveFuelSource(index, isAdditional)}
          sx={{ mt: 1 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default FuelSourceItem; 